import { Card, CardBody, Flex, VStack } from '@chakra-ui/react';
import {
  ListingComparableSetFragment,
  ListingFragment,
  MlsListingObject,
  PropertyDetailsFragment,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';
import { ComparableColumn } from '../../Listings/Comparables/ComparableColumns/ComparableColumn';
import {
  getPropertyDetails,
  transformPropertyDetailsToMlsListing,
} from '../../Listings/ListingPage/utils';
import { ExploreProvider } from '../../MapSearch/ExploreContext';
import { ComparablesContainer } from '../styledComponents';
import { CompsMap } from './CompsMap/CompsMap';
import { CompsSummary } from './CompsSummary';

interface ViewCompsProps {
  listing?: ListingFragment | PublicListingFragment;
  propertyDetails?: PropertyDetailsFragment;
  comparableSet?: ListingComparableSetFragment;
  comps: ComparableState[];
}

export const ViewComps = ({
  listing,
  propertyDetails,
  comparableSet,
  comps,
}: ViewCompsProps) => {
  const normalizedPropertyDetails = useMemo(
    () => getPropertyDetails(listing, propertyDetails),
    [listing, propertyDetails]
  );

  // If the listing is off market, we need to transform the property details to an MlsListingFragment structure.
  // This is a hack to allow PropertyDetails to be used with the ComparableColumn components.
  const mlsListing = listing
    ? listing.mlsListing
    : transformPropertyDetailsToMlsListing(propertyDetails);

  return (
    <ExploreProvider showClosedStatus skipMlsListingsSearch>
      <VStack gap={{ base: 0, md: 4 }} width="100%">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 0, md: 4 }}
          height={{ base: '100%', md: '40vh' }}
          position="relative"
          width="100%"
        >
          <Flex
            flexBasis={{ base: undefined, md: 0 }}
            flexGrow={{ base: undefined, md: 1 }}
            height={{ base: '40vh', md: '100%' }}
          >
            <CompsMap
              comparableSet={comparableSet}
              isMini={false}
              mainListing={normalizedPropertyDetails}
            />
          </Flex>
          <Flex flexBasis={0} flexGrow={1} height="100%">
            <Card borderRadius={{ base: '0px', md: '12px' }} width="100%">
              <CardBody>
                <CompsSummary
                  comparableSet={comparableSet}
                  listing={normalizedPropertyDetails}
                />
              </CardBody>
            </Card>
          </Flex>
        </Flex>
        <Flex pl={{ base: 2, md: 0 }} py={{ base: 2, md: 0 }} width="100%">
          <ComparablesContainer width="100%">
            <ComparableColumn
              isMainListing
              isEditMode={false}
              mainMlsListing={mlsListing as MlsListingObject}
              mlsListing={mlsListing as MlsListingObject}
              showAdjustments={true}
              showNotes={true}
            />
            {comps.map((comp) => (
              <ComparableColumn
                key={comp.mlsListing.id}
                comparableSet={comparableSet}
                isEditMode={false}
                isMainListing={false}
                mainMlsListing={mlsListing as MlsListingObject}
                mlsListing={comp.mlsListing as MlsListingObject}
                showAdjustments={true}
                showNotes={true}
              />
            ))}
          </ComparablesContainer>
        </Flex>
      </VStack>
    </ExploreProvider>
  );
};
